import { faFull, faPlain } from 'utilities/helper/format'

/**
 * نظمِ حق عضویت — one cell per Jalali month, GitHub-contributions shaped.
 *
 * «۳۲ از ۳۵ پرداخت شده» is a number. WHICH three were missed — and whether
 * they are old history or the last two months — is a judgement, and only the
 * shape shows it. That is the whole reason this block exists next to the
 * summary line rather than instead of it.
 *
 * Cells are NOT tappable, deliberately. حق عضویت is monthly in ~۹۹٪ of funds,
 * so a cell IS one payment; there is nothing behind it a tap could reveal,
 * and a tap target that opens nothing is worse than none.
 */

const CELL: Record<TPortionCell, { fill: string; border: string; label: string }> = {
    // «به‌موقع» and «پرداخت‌نشده» are the owner's سبز/قرمز. The other two
    // paid states get their own colours because «دیر پرداخت کرد» and
    // «اصلاً نداد» are different facts about a member and a manager acts
    // differently on each.
    ontime: { fill: '#74B743', border: '#74B743', label: 'به‌موقع' },
    late: { fill: '#FFB114', border: '#FFB114', label: 'با تأخیر' },
    partial: { fill: '#C7E3A6', border: '#9CC978', label: 'ناقص' },
    unpaid: { fill: '#F24C3D', border: '#F24C3D', label: 'پرداخت‌نشده' },
    none: { fill: '#EDEFF2', border: '#EDEFF2', label: 'بدون تعهد' },
    future: { fill: '#FFFFFF', border: '#D7DBE0', label: 'سررسید نرسیده' },
}

// Persian does not abbreviate month names — «ار» for اردیبهشت is not a thing.
// Numbers are what a calendar header uses.
const MONTH_NUMBERS = Array.from({ length: 12 }, (_, i) => faPlain.format(i + 1))

const LEGEND: TPortionCell[] = [
    'ontime',
    'late',
    'partial',
    'unpaid',
    'none',
    'future',
]

function summarySentence(grid: IMemberPortionGrid) {
    const { matured, paid, late, partial, unpaid, mean_days_late } = grid.summary
    if (matured === 0) return 'هنوز حق عضویتی سررسید نشده است.'
    const parts = [
        `${faPlain.format(paid)} از ${faPlain.format(matured)} ماه پرداخت شده`,
    ]
    if (late > 0) {
        parts.push(
            `${faPlain.format(late)} بار با تأخیر` +
                (mean_days_late > 0
                    ? ` (به‌طور میانگین ${faPlain.format(mean_days_late)} روز)`
                    : '')
        )
    }
    if (partial > 0) parts.push(`${faPlain.format(partial)} پرداخت ناقص`)
    if (unpaid > 0) parts.push(`${faPlain.format(unpaid)} ماه پرداخت‌نشده`)
    // Persian comma, never a middle dot: a «·» against a digit reads as an
    // extra zero at phone size.
    return parts.join('، ') + '.'
}

export default function PortionGrid({
    grid,
    unpaidAmount = 0,
}: {
    grid: IMemberPortionGrid
    /** حق عضویت سررسیدشده و پرداخت‌نشده, in Toman — `portions.unpaid_amount` */
    unpaidAmount?: number
}) {
    return (
        <section className=" rounded-2xl border border-stroke bg-white p-4 shadow-card">
            <p className=" text-sm font-bold text-text-body">نظمِ حق عضویت</p>
            {grid.monthly_amount > 0 && (
                <p className=" mt-1 text-[11px] text-text-secondary">
                    هر خانه یک ماه است، حق عضویت ماهانه{' '}
                    {faFull.format(grid.monthly_amount)} تومان.
                </p>
            )}
            <p className=" mt-2 text-xs leading-6 text-text-body">
                {summarySentence(grid)}
            </p>
            {/* The grid says WHICH months went unpaid; this says how much
                they come to. The old «سابقه مالی عضو» printed «مجموع بدهی»
                on its uncollapsed top half, and pwa#61 sends members here
                instead of there — a page that draws red squares and never
                totals them is a step back for the reader it is now
                written for.

                Its own line, not another clause on the sentence above:
                appended there it read «۳ ماه پرداخت‌نشده، مجموعاً ...
                پرداخت‌نشده», the same word twice running. And this is a
                sum over every matured حق عضویت — partial months included —
                so it is not the same fact as the unpaid COUNT and should
                not look like a restatement of it. */}
            {unpaidAmount > 0 && (
                <p className=" mt-1 text-xs font-bold text-danger-on">
                    مجموع حق عضویت پرداخت‌نشده: {faFull.format(unpaidAmount)}{' '}
                    تومان
                </p>
            )}

            <div className=" mt-3">
                <div
                    className=" mb-1 grid gap-[3px]"
                    style={{ gridTemplateColumns: '26px repeat(12, minmax(0, 1fr))' }}
                >
                    <span />
                    {MONTH_NUMBERS.map((month) => (
                        <span
                            key={month}
                            className=" text-center text-[9px] text-text-muted"
                        >
                            {month}
                        </span>
                    ))}
                </div>
                <div className=" flex flex-col gap-[3px]">
                    {grid.years.map((row) => (
                        <div
                            key={row.year}
                            className=" grid items-center gap-[3px]"
                            style={{
                                gridTemplateColumns: '26px repeat(12, minmax(0, 1fr))',
                            }}
                        >
                            <span className=" text-[9px] text-text-secondary">
                                {faPlain.format(row.year)}
                            </span>
                            {row.cells.map((cell, index) => (
                                <span
                                    key={index}
                                    title={CELL[cell].label}
                                    className=" h-[22px] w-full rounded"
                                    style={{
                                        background: CELL[cell].fill,
                                        border: `1px solid ${CELL[cell].border}`,
                                    }}
                                />
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            <div className=" mt-3 flex flex-wrap gap-2.5">
                {LEGEND.map((key) => (
                    <span
                        key={key}
                        className=" flex items-center gap-1.5 text-[10px] text-text-secondary"
                    >
                        <span
                            className=" h-2.5 w-2.5 rounded-[3px]"
                            style={{
                                background: CELL[key].fill,
                                border: `1px solid ${CELL[key].border}`,
                            }}
                        />
                        {CELL[key].label}
                    </span>
                ))}
            </div>

            {!!grid.hidden_history?.years && (
                <p className=" mt-3 text-[11px] leading-5 text-text-secondary">
                    پیش از این هم {faPlain.format(grid.hidden_history.years)} سال
                    عضو بوده
                    {grid.hidden_history.late_count
                        ? `، ${faPlain.format(
                              grid.hidden_history.late_count
                          )} بار با تأخیر`
                        : ''}
                    .
                </p>
            )}
        </section>
    )
}
